/**
 * C0-LOOK P3 sample, a clone-only experiment, switched by the `masterLook` setting. Applied to every scenery
 * sprite (trees, rocks, bushes, props and, before they are cut into strips, buildings) once, when the
 * props are built; figures and UI are not touched.
 *
 *  1. Saturation cap: foliage and lacquer far above the ground's saturation are pulled down (S <= 0.58).
 *  2. One master ramp set: every pixel snaps to the nearest colour of 11 hue-shifted ramps (cool
 *     violet in the shade, warm yellow in the light), so sprites of different generations share colours
 *     and speckle noise merges into flat regions.
 *  3. Selective coloured outline: the near-black outline ring becomes a darker shade of the sprite's own
 *     neighbouring colour; on the sun side (up and right edges, the key light is upper right) it is one
 *     step lighter, on the shade side darker. Outline stays 1 px.
 *  The "one pixel size" rule is only approximated: the palette snap merges sub-pixel speckle; nothing is resampled.
 */
import { settings } from '../settings';
import { canvas, ctx2d } from './pixel';
import type { Sprite } from './scenery';
import './masterLook.setting';

const hex = (h: string): [number, number, number] => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];

/** Dark to light, hue-shifted: shadows lean blue-violet, lights lean yellow. */
const RAMP_HEX = [
  ['#12261f', '#1f4636', '#2f6048', '#4f8d64', '#86bf7a'], // foliage
  ['#2a4a2c', '#3e6b38', '#5c8a44', '#86a85c', '#b3c880'], // lawn
  ['#2a1c18', '#4a2e26', '#6a4332', '#8f6445', '#b88c62'], // timber and earth
  ['#2c2a30', '#4a4650', '#6f6a6c', '#988f88', '#c4b8a8'], // stone
  ['#1c2234', '#2c3a56', '#44587a', '#6a82a0', '#98b0c8'], // slate roof
  ['#3a1020', '#6e1a28', '#a62a30', '#d4513c', '#f08a5a'], // red lacquer, tile
  ['#4a3414', '#8a6420', '#c99a30', '#eec85a', '#fff0a8'], // gold
  ['#5a1f3a', '#a9486c', '#d9708f', '#f2a0b9', '#ffd9e4'], // blossom
  ['#4a4458', '#8a829a', '#c4bcc4', '#e8e0dc', '#fff8f0'], // cloth, plaster
  ['#1a2a4a', '#284f7d', '#3f83a6', '#7fbcbe', '#cfe4e2'], // blue, water
  ['#0e0c14', '#1b1722', '#2a2430', '#3a3040', '#4a3c50'], // ink
] as const;
const PALETTE = RAMP_HEX.flatMap((r, ri) => r.map((h, step) => ({ rgb: hex(h), ri, step })));

const SAT_CAP = 0.58;
const snapCache = new Map<number, number>();

function capSat(r: number, g: number, b: number): [number, number, number] {
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b);
  const l = (mx + mn) / 2;
  const d = mx - mn;
  if (d === 0) return [r, g, b];
  const s = d / (255 - Math.abs(2 * l - 255));
  if (s <= SAT_CAP) return [r, g, b];
  const k = SAT_CAP / s; // scale the chroma around the grey of the same lightness
  return [l + (r - l) * k, l + (g - l) * k, l + (b - l) * k];
}

/** Index of the nearest master colour (redmean distance), cached per input colour. */
function nearest(r: number, g: number, b: number): number {
  const key = (r << 16) | (g << 8) | b;
  const hit = snapCache.get(key);
  if (hit !== undefined) return hit;
  const [cr, cg, cb] = capSat(r, g, b);
  let best = 0, bd = 1e12;
  for (let i = 0; i < PALETTE.length; i++) {
    const [pr, pg, pb] = PALETTE[i]!.rgb;
    const rm = (cr + pr) / 2;
    const dr = cr - pr, dg = cg - pg, db = cb - pb;
    const d = (2 + rm / 256) * dr * dr + 4 * dg * dg + (2 + (255 - rm) / 256) * db * db;
    if (d < bd) { bd = d; best = i; }
  }
  snapCache.set(key, best);
  return best;
}

const lum = (r: number, g: number, b: number): number => 0.299 * r + 0.587 * g + 0.114 * b;

/** The sprite with the master look: capped, snapped, selectively outlined. Same size, same anchors. */
function process(src: HTMLCanvasElement): HTMLCanvasElement {
  const w = src.width, h = src.height;
  const g = ctx2d(src);
  const img = g.getImageData(0, 0, w, h);
  const s = img.data;
  const idx = new Int16Array(w * h).fill(-1); // palette index per opaque pixel
  const edge = new Uint8Array(w * h); // bit0 up open, bit1 right open, bit2 down open, bit3 left open
  const solid = (x: number, y: number) => (x < 0 || y < 0 || x >= w || y >= h ? true : s[(y * w + x) * 4 + 3]! >= 128);
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const k = (y * w + x) * 4;
      if (s[k + 3]! < 128) continue;
      let m = 0;
      if (!solid(x, y - 1)) m |= 1;
      if (!solid(x + 1, y)) m |= 2;
      if (!solid(x, y + 1)) m |= 4;
      if (!solid(x - 1, y)) m |= 8;
      edge[y * w + x] = m;
      const dark = lum(s[k]!, s[k + 1]!, s[k + 2]!) < 62;
      // outline pixels are decided below; everything else snaps to the master set now
      if (m && dark) continue;
      idx[y * w + x] = nearest(s[k]!, s[k + 1]!, s[k + 2]!);
    }
  const out = canvas(w, h);
  const og = ctx2d(out);
  const o = og.createImageData(w, h);
  const d = o.data;
  for (let y = 0; y < h; y++)
    for (let x = 0; x < w; x++) {
      const p = y * w + x;
      const k = p * 4;
      if (s[k + 3]! < 128) continue;
      let rgb: readonly [number, number, number];
      if (idx[p]! >= 0) rgb = PALETTE[idx[p]!]!.rgb;
      else {
        // an outline pixel: find the ramp of the nearest inside neighbour, take a step toward dark
        // (shade side) or stay one step lighter (sun side: edges open to the top or the right)
        let ri = -1, step = 2;
        for (const [dx, dy] of [[0, 1], [-1, 0], [1, 0], [0, -1], [-1, 1], [1, 1], [-1, -1], [1, -1]] as const) {
          const qx = x + dx, qy = y + dy;
          if (qx < 0 || qy < 0 || qx >= w || qy >= h) continue;
          const q = idx[qy * w + qx]!;
          if (q >= 0) { ri = PALETTE[q]!.ri; step = PALETTE[q]!.step; break; }
        }
        if (ri < 0) rgb = PALETTE[RAMP_HEX[0].length * 10]!.rgb; // ink
        else {
          const sun = (edge[p]! & 3) !== 0 && (edge[p]! & 12) === 0;
          const to = Math.max(0, Math.min(4, step - (sun ? 2 : 3)));
          rgb = PALETTE[ri * 5 + to]!.rgb;
        }
      }
      d[k] = rgb[0];
      d[k + 1] = rgb[1];
      d[k + 2] = rgb[2];
      d[k + 3] = 255;
    }
  og.putImageData(o, 0, 0);
  return out;
}

const DONE = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();
const MADE = new WeakSet<HTMLCanvasElement>();

/** A scenery image in the master look (itself when the setting is off); the same input gives the same output. */
export function masterLookImg(c: HTMLCanvasElement): HTMLCanvasElement {
  if (!settings.masterLook || MADE.has(c) || c.width * c.height === 0) return c;
  let r = DONE.get(c);
  if (!r) {
    r = process(c);
    DONE.set(c, r);
    MADE.add(r);
  }
  return r;
}

/** A building or tree sprite in the master look, anchors kept. */
export function masterLookSprite<T extends Sprite>(sp: T): T {
  const img = masterLookImg(sp.img);
  return img === sp.img ? sp : { ...sp, img };
}
